import { DestroyRef, Injectable, computed, signal, effect, inject } from '@angular/core';
import { StreamThreadSummaryDto } from '../api/models/inquiry.dto';

export interface StoredThread {
  threadId: string;
  token: string;
  propertyName?: string;
  guestName?: string;
  createdAt: string;
  checkIn?: string;
  checkOut?: string;
  numberOfGuests?: number;
  numberOfChildren?: number;
  numberOfPets?: number;
  totalPrice?: number;
  lastMessage?: string;
  lastMessageSender?: 'guest' | 'host';
  lastMessageAt?: string;
  unreadCount?: number;
}

const STORAGE_KEY = 'nadmorzem_inquiry_threads';

@Injectable({ providedIn: 'root' })
export class InquiryThreadsService {
  private readonly _threads = signal<StoredThread[]>(this.loadFromStorage());
  private readonly _activeThreadId = signal<string | null>(null);

  readonly threads = this._threads.asReadonly();
  readonly activeThreadId = this._activeThreadId.asReadonly();
  readonly count = computed(() => this._threads().length);
  readonly isEmpty = computed(() => this._threads().length === 0);
  readonly totalUnread = computed(() =>
    this._threads().reduce((sum, t) => sum + (t.unreadCount ?? 0), 0),
  );

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.saveToStorage(this._threads());
    });
    this.followClearInOtherTabs();
  }

  setActiveThread(threadId: string | null): void {
    this._activeThreadId.set(threadId);
  }

  /**
   * Apply thread summaries received from stream.init SSE event.
   * Updates unreadCount, lastMessage, lastMessageSender and propertyName
   * for each thread that the backend returned data for.
   */
  applyStreamSummaries(summaries: StreamThreadSummaryDto[]): void {
    this._threads.update(threads => {
      const updated = threads.map(t => {
        const summary = summaries.find(s => s.threadId === t.threadId);
        if (!summary) return t;
        return {
          ...t,
          unreadCount: summary.unreadCount,
          ...(summary.lastMessage != null
            ? { lastMessage: summary.lastMessage, lastMessageSender: summary.lastMessageSender ?? undefined }
            : {}),
          ...(summary.lastMessageAt != null ? { lastMessageAt: summary.lastMessageAt } : {}),
          ...(summary.propertyName ? { propertyName: summary.propertyName } : {}),
        };
      });
      // Sort by lastMessageAt descending (newest first), fallback to createdAt
      return updated.sort((a, b) => {
        const timeA = a.lastMessageAt || a.createdAt;
        const timeB = b.lastMessageAt || b.createdAt;
        return timeB.localeCompare(timeA);
      });
    });
  }

  addThread(thread: StoredThread): void {
    this._threads.update(threads => {
      const existing = threads.find(t => t.threadId === thread.threadId);
      if (existing) {
        return threads.map(t =>
          t.threadId === thread.threadId ? { ...t, token: thread.token } : t,
        );
      }
      return [thread, ...threads];
    });
  }

  updateToken(threadId: string, token: string): void {
    this._threads.update(threads =>
      threads.map(t =>
        t.threadId === threadId ? { ...t, token } : t,
      ),
    );
  }

  updateLastMessage(threadId: string, content: string, senderType: 'guest' | 'host'): void {
    const now = new Date().toISOString();
    this._threads.update(threads => {
      const updated = threads.map(t =>
        t.threadId === threadId
          ? { ...t, lastMessage: content, lastMessageSender: senderType, lastMessageAt: now }
          : t,
      );
      // Move thread with new message to top (like a chat app)
      const idx = updated.findIndex(t => t.threadId === threadId);
      if (idx > 0) {
        const [thread] = updated.splice(idx, 1);
        updated.unshift(thread);
      }
      return updated;
    });
  }

  getThread(threadId: string): StoredThread | undefined {
    return this._threads().find(t => t.threadId === threadId);
  }

  incrementUnread(threadId: string): void {
    this._threads.update(threads =>
      threads.map(t =>
        t.threadId === threadId
          ? { ...t, unreadCount: (t.unreadCount ?? 0) + 1 }
          : t,
      ),
    );
  }

  resetUnread(threadId: string): void {
    this._threads.update(threads =>
      threads.map(t =>
        t.threadId === threadId ? { ...t, unreadCount: 0 } : t,
      ),
    );
  }

  removeThread(threadId: string): void {
    this._threads.update(threads =>
      threads.filter(t => t.threadId !== threadId),
    );
  }

  /**
   * „Zapomnij to urządzenie”: forgets every conversation kept in this browser
   * (guest tokens, names, dates, last messages). Memory and localStorage are
   * emptied at once — the save effect runs only with the next change detection.
   * The background SSE stream follows the empty list and closes
   * (InquiryEventsService); other tabs drop their copy on the `storage` event.
   */
  clear(): void {
    this._threads.set([]);
    this.saveToStorage([]);
  }

  /**
   * Another tab emptied the stored list („Zapomnij to urządzenie”, storage
   * cleared): this tab drops its in-memory copy too — otherwise its next write
   * (stream summary, new message) would store the guest tokens again. Other
   * changes of another tab are NOT merged: every tab applies the same stream
   * events itself, so merging would count unread messages twice.
   */
  private followClearInOtherTabs(): void {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    const onStorage = (event: StorageEvent): void => {
      // `key === null` → another tab cleared the whole storage.
      if (event.key !== null && event.key !== STORAGE_KEY) return;
      if (this._threads().length === 0 || this.loadFromStorage().length > 0) return;
      this._threads.set([]);
    };
    window.addEventListener('storage', onStorage);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', onStorage));
  }

  private loadFromStorage(): StoredThread[] {
    if (typeof localStorage === 'undefined') return [];

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (t): t is StoredThread =>
              typeof t === 'object' &&
              typeof t.threadId === 'string' &&
              typeof t.token === 'string',
          );
        }
      }
    } catch {
      console.warn('Failed to load inquiry threads from localStorage');
    }
    return [];
  }

  private saveToStorage(threads: StoredThread[]): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(threads));
    } catch {
      console.warn('Failed to save inquiry threads to localStorage');
    }
  }
}
